import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { IconChevronDown, IconLogout, IconUser } from '@tabler/icons-react';
import { WP_ICON_NAV } from '../icons/constants.js';
import { cx } from '../utils/cx.js';
import { ProximityScope } from '../primitives/ProximityHighlight.js';
import clusterStyles from './NavCluster.module.css';
import styles from './NavAccountMenu.module.css';

const OPEN_MS = 80;
const CLOSE_MS = 220;

export type NavAccountMenuItem = {
  key?: string;
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
};

export type NavAccountMenuProps = {
  /** Shown under the display name; omit to hide. */
  email?: string;
  /** Shown above email in the panel identity block. */
  displayName?: string;
  /** Optional avatar next to name/email in the panel. */
  identityAvatar?: ReactNode;
  onLogout: () => void;
  /** Extra actions between email and logout. */
  extraItems?: NavAccountMenuItem[];
  /** Non-action row(s) below email (e.g. preference toggles). */
  panelExtra?: ReactNode;
  /** Override trigger icon (default: user). */
  triggerIcon?: ReactNode;
  /** `aria-label` / `title` on trigger. */
  menuLabel?: string;
};

export function NavAccountMenu({
  email,
  displayName,
  identityAvatar,
  onLogout,
  extraItems,
  panelExtra,
  triggerIcon,
  menuLabel = 'Compte',
}: NavAccountMenuProps) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const openT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  const clearTimers = useCallback(() => {
    if (openT.current) clearTimeout(openT.current);
    if (closeT.current) clearTimeout(closeT.current);
    openT.current = null;
    closeT.current = null;
  }, []);

  const scheduleOpen = useCallback(() => {
    clearTimers();
    openT.current = setTimeout(() => setOpen(true), OPEN_MS);
  }, [clearTimers]);

  const scheduleClose = useCallback(() => {
    if (pinned) return;
    clearTimers();
    closeT.current = setTimeout(() => setOpen(false), CLOSE_MS);
  }, [clearTimers, pinned]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const closeMenu = useCallback(() => {
    clearTimers();
    setPinned(false);
    setOpen(false);
  }, [clearTimers]);

  const togglePinnedMenu = useCallback(() => {
    clearTimers();
    if (open && pinned) {
      closeMenu();
      return;
    }
    setPinned(true);
    setOpen(true);
  }, [clearTimers, closeMenu, open, pinned]);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: PointerEvent) => {
      const root = rootRef.current;
      if (!root?.contains(e.target as Node)) closeMenu();
    };
    document.addEventListener('pointerdown', onDocPointer, true);
    return () => document.removeEventListener('pointerdown', onDocPointer, true);
  }, [closeMenu, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      closeMenu();
      triggerRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeMenu, open]);

  const ni = WP_ICON_NAV;
  const name = displayName?.trim() || null;

  return (
    <div
      ref={rootRef}
      className={cx(clusterStyles.wrap, open && clusterStyles.open)}
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
      onFocusCapture={() => {
        if (!pinned) scheduleOpen();
      }}
      onBlurCapture={(e) => {
        if (pinned) return;
        const t = e.relatedTarget as Node | null;
        if (t && e.currentTarget.contains(t)) return;
        scheduleClose();
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className={cx(clusterStyles.trigger, styles.triggerAccount)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={menuLabel}
        title={menuLabel}
        onClick={togglePinnedMenu}
      >
        <span className={clusterStyles.triggerIcon}>
          {triggerIcon ?? <IconUser {...ni} aria-hidden />}
        </span>
        <IconChevronDown className={clusterStyles.chev} stroke={1.5} aria-hidden />
      </button>
      {open ? (
        <div id={panelId} className={styles.panel}>
          <div className={styles.identity}>
            <div className={styles.identityRow}>
              {identityAvatar ? (
                <div className={styles.identityAvatar} aria-hidden>
                  {identityAvatar}
                </div>
              ) : null}
              <div className={styles.identityText}>
                {name ? <div className={styles.displayName}>{name}</div> : null}
                {email ? <div className={styles.email}>{email}</div> : null}
              </div>
            </div>
            {panelExtra}
          </div>
          <ProximityScope axis="y" className={styles.actionsProximity}>
            {extraItems?.length ? (
              <div className={styles.actions}>
                {extraItems.map((item) => (
                  <button
                    key={item.key ?? item.label}
                    type="button"
                    className={cx(clusterStyles.item, styles.menuAction)}
                    data-wp-proximity-item=""
                    onClick={() => {
                      closeMenu();
                      item.onSelect();
                    }}
                  >
                    {item.icon ? <span className={clusterStyles.itemIcon}>{item.icon}</span> : null}
                    <span className={clusterStyles.itemLabel}>{item.label}</span>
                  </button>
                ))}
              </div>
            ) : null}
            <button
              type="button"
              className={cx(clusterStyles.item, styles.menuAction, styles.logoutAction)}
              data-wp-proximity-item=""
              onClick={() => {
                closeMenu();
                onLogout();
              }}
            >
              <span className={clusterStyles.itemIcon}>
                <IconLogout {...ni} aria-hidden />
              </span>
              <span className={clusterStyles.itemLabel}>Déconnexion</span>
            </button>
          </ProximityScope>
        </div>
      ) : null}
    </div>
  );
}
